import LoadingButton from '@mui/lab/LoadingButton';
import {
    alpha,
    Box,
    Button,
    Checkbox,
    DialogContent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useDialog } from 'muibox';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { SextforceAutoRepostWithLatestRepostLog } from '../../../../hooks/useSextforceAutoRepost';
import DialogAdaptive from '../../../common/DialogAdaptive';
import StyledCardTitleBar from '../../../common/StyledCardTitleBar';
import StyledDialogActions from '../../../common/StyledDialogActions';

type Props = {
    active: boolean;
    autoReposts: SextforceAutoRepostWithLatestRepostLog[];
    refetchAutoReposts: () => void;
    bulkDeleteAutoReposts: (autoRepostIds: string[], active: boolean) => Promise<{ success: boolean }>;
    bulkDeleteAutoRepostsLoading: boolean;
    open: boolean;
    setOpen: (open: boolean) => void;
};

type SortField = 'name' | 'repostCount' | 'nextRepostDate';
type SortDirection = 'asc' | 'desc';

const SextforceAutoRepostBulkDeleteDialog = (props: Props) => {
    const { active, autoReposts, refetchAutoReposts, bulkDeleteAutoReposts, bulkDeleteAutoRepostsLoading, open, setOpen } = props;
    const { enqueueSnackbar } = useSnackbar();
    const dialog = useDialog();

    const [selectedAutoRepostsIds, setSelectedAutoRepostsIds] = useState<string[]>([]);
    const [selectedAutoReposts, setSelectedAutoReposts] = useState<SextforceAutoRepostWithLatestRepostLog[]>([]);

    // Sorting state
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    // Sort the auto reposts based on current sort settings
    const sortedAutoReposts = useMemo(() => {
        return [...autoReposts].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortField) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'repostCount':
                    aValue = a.repostCount;
                    bValue = b.repostCount;
                    break;
                case 'nextRepostDate':
                    aValue = new Date(a.nextRepostDate).getTime();
                    bValue = new Date(b.nextRepostDate).getTime();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [autoReposts, sortField, sortDirection]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleClose = () => {
        // Reset selected auto reposts when the dialog is closed
        setSelectedAutoReposts([]);
        setSelectedAutoRepostsIds([]);
        // Close the dialog
        setOpen(false);
    };

    // Handle delete action
    const handleDelete = () => {
        dialog
            .confirm({
                title: 'Delete Auto Reposts',
                message: `Are you sure you want to delete ${selectedAutoReposts.length.toLocaleString()} Auto Reposts?`,
                ok: {
                    text: 'Delete',
                    color: 'error',
                    variant: 'contained',
                },
                cancel: {
                    text: 'Cancel',
                    color: 'secondary',
                    variant: 'text',
                },
            })
            .then(() => {
                // Perform delete action
                bulkDeleteAutoReposts(selectedAutoRepostsIds, active)
                    .then(res => {
                        if (res.success) {
                            enqueueSnackbar(active ? 'Auto Reposts scheduled for deleting' : 'Auto Reposts deleted successfully', {
                                variant: 'success',
                            });

                            refetchAutoReposts();
                            handleClose();
                        } else {
                            enqueueSnackbar('Failed to delete Auto Reposts', { variant: 'error' });
                        }
                    })
                    .catch(err => {
                        console.error(err);
                        enqueueSnackbar('Failed to delete Auto Reposts', { variant: 'error' });
                    });
            })
            .catch(() => {
                // Handle cancel
            });
    };

    return (
        <DialogAdaptive open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <StyledCardTitleBar title={`Auto Reposts Bulk Delete (${active ? 'Active' : 'Inactive'})`} />
            <DialogContent sx={{ pl: 0, pr: 0 }}>
                <Stack direction="column" spacing={2}>
                    <Typography variant="body1" color="text.secondary" pl={2} pr={2}>
                        Select the Auto Reposts you want to {active ? 'schedule for deletion' : 'delete'}. You can select all Auto Reposts
                        by checking the checkbox in the header.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" pl={2} pr={2}>
                        {selectedAutoReposts.length.toLocaleString()} Auto Reposts selected
                    </Typography>

                    <Box
                        sx={{
                            width: '100%',
                            overflow: 'hidden',
                            backgroundColor: theme => alpha(theme.palette.error.main, 0.1),
                        }}
                    >
                        <TableContainer
                            sx={{
                                maxHeight: 400,
                                // Reduce the padding between the cells
                                '& .MuiTableCell-root': {
                                    padding: '4px 8px',
                                },
                                // remove the left padding of the first cell
                                '& .MuiTableCell-root:first-of-type': {
                                    paddingLeft: 0,
                                },
                                // remove the right padding of the last cell
                                '& .MuiTableCell-root:last-of-type': {
                                    paddingRight: 2,
                                },
                            }}
                        >
                            <Table
                                size="small"
                                // stickyHeader
                                sx={{
                                    // Pin the header row
                                    '& thead th': {
                                        position: 'sticky',
                                        top: 0,
                                        backgroundColor: 'rgb(239, 210, 208)', // (theme: Theme) => alpha(theme.palette.error.main, 0.2),
                                        zIndex: 1,
                                    },
                                    // Pin the first column
                                    '& th:first-of-type, & td:first-of-type': {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 1,
                                    },
                                }}
                            >
                                <TableHead
                                    sx={{
                                        backgroundColor: 'rgb(239, 210, 208)', // (theme: Theme) => alpha(theme.palette.error.main, 0.2),
                                        // pin the first column
                                        '& th:first-of-type': {
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                        },
                                    }}
                                >
                                    <TableRow
                                        sx={{
                                            // every cell in the header row
                                            '& th': {
                                                backgroundColor: theme => theme.palette.background.paper,
                                                color: theme => theme.palette.text.primary,
                                                fontWeight: 'bold',
                                            },
                                        }}
                                    >
                                        <TableCell width={50} align="center">
                                            <Checkbox
                                                indeterminate={
                                                    selectedAutoRepostsIds.length > 0 &&
                                                    selectedAutoRepostsIds.length < sortedAutoReposts.length
                                                }
                                                checked={selectedAutoReposts.length === sortedAutoReposts.length}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    if (e.target.checked) {
                                                        setSelectedAutoRepostsIds(sortedAutoReposts.map(autoRepost => autoRepost._id!));
                                                        setSelectedAutoReposts(sortedAutoReposts);
                                                    } else {
                                                        setSelectedAutoRepostsIds([]);
                                                        setSelectedAutoReposts([]);
                                                    }
                                                }}
                                                color="error"
                                                size="small"
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={sortField === 'name'}
                                                direction={sortField === 'name' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('name')}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'inherit !important',
                                                    },
                                                }}
                                            >
                                                Auto Repost
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" width={50}>
                                            <TableSortLabel
                                                active={sortField === 'repostCount'}
                                                direction={sortField === 'repostCount' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('repostCount')}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'inherit !important',
                                                    },
                                                }}
                                            >
                                                Reposts
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell width={150}>
                                            <TableSortLabel
                                                active={sortField === 'nextRepostDate'}
                                                direction={sortField === 'nextRepostDate' ? sortDirection : 'asc'}
                                                onClick={() => handleSort('nextRepostDate')}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    '& .MuiTableSortLabel-icon': {
                                                        color: 'inherit !important',
                                                    },
                                                }}
                                            >
                                                Next Repost
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {sortedAutoReposts.map(autoRepost => (
                                        <TableRow
                                            key={autoRepost._id}
                                            sx={{
                                                '&:last-child td, &:last-child th': {
                                                    border: 0,
                                                },
                                                backgroundColor: theme =>
                                                    selectedAutoRepostsIds.includes(autoRepost._id!)
                                                        ? alpha(theme.palette.error.main, 0.2)
                                                        : 'transparent',
                                            }}
                                        >
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={selectedAutoRepostsIds.includes(autoRepost._id!)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        if (e.target.checked) {
                                                            setSelectedAutoRepostsIds([...selectedAutoRepostsIds, autoRepost._id!]);
                                                            setSelectedAutoReposts([...selectedAutoReposts, autoRepost]);
                                                        } else {
                                                            setSelectedAutoRepostsIds(
                                                                selectedAutoRepostsIds.filter(id => id !== autoRepost._id),
                                                            );
                                                            setSelectedAutoReposts(
                                                                selectedAutoReposts.filter(ar => ar._id !== autoRepost._id),
                                                            );
                                                        }
                                                    }}
                                                    color="error"
                                                    size="small"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.primary" noWrap>
                                                    {autoRepost.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    {autoRepost.repostCount.toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    {moment(autoRepost.nextRepostDate).format('DD/MM/YYYY hh:mm A')}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Stack>
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={handleClose} color="secondary" variant="text">
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color="error"
                    size="small"
                    loading={bulkDeleteAutoRepostsLoading}
                    disabled={selectedAutoRepostsIds.length === 0}
                    onClick={() => {
                        handleDelete();
                    }}
                >
                    <span>Delete Selected</span>
                </LoadingButton>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default SextforceAutoRepostBulkDeleteDialog;
